import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Paper,
  Grid,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { headers } from "../../utils/utils";

const FieldsConfig = () => {
  const [fields, setFields] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentField, setCurrentField] = useState({
    type: "input",
    label: "",
    options: "",
  });
  const [editIndex, setEditIndex] = useState(null);

  const storedBoardId = localStorage.getItem("board");

  // 🔹 Load fields from backend
  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_TICKET_SERVER}/ticket/dynamicFieldsLoad/${storedBoardId}`,
          {
            method: "GET",
            headers: headers(),
          }
        );

        if (response.ok) {
          const data = await response.json();
          setFields(data || []);
        }
      } catch (err) {
        console.error("Error loading fields:", err);
      }
    };

    if (storedBoardId) {
      fetchFields();
    }
  }, [storedBoardId]);

  const handleOpen = () => {
    setCurrentField({ type: "input", label: "", options: "" });
    setEditIndex(null);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  // 🔹 Save or Update field locally + backend
  const handleSave = () => {
    if (!currentField.label) return alert("Label is required!");

    const newField = {
      type: currentField.type,
      label: currentField.label,
      options:
        currentField.type === "select"
          ? currentField.options.split(",").map((o) => o.trim())
          : [],
    };

    let updated;
    if (editIndex !== null) {
      updated = [...fields];
      updated[editIndex] = newField;
    } else {
      updated = [...fields, newField];
    }

    setFields(updated);
    handleSaveToBackend(updated); // ✅ pass updated array

    handleClose();
  };

  const handleEdit = (index) => {
    const field = fields[index];
    setCurrentField({
      type: field.type,
      label: field.label,
      options: field.options.join(", "),
    });
    setEditIndex(index);
    setOpen(true);
  };

  const handleDelete = (index) => {
    const updated = fields.filter((_, i) => i !== index);
    setFields(updated);
    handleSaveToBackend(updated); // ✅ pass updated array
  };

  // 🔹 Save to backend
  const handleSaveToBackend = async (updatedFields) => {
    const payload = {
      boardId: storedBoardId, // keep boardId for backend mapping
      fields: updatedFields,  // ✅ always latest fields
    };

    try {
      const response = await fetch(
        process.env.REACT_APP_TICKET_SERVER + "/ticket/dynamicFieldsSave",
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save configuration");
      }

      console.log("✅ Configuration saved", payload);
    } catch (err) {
      console.error(err);
      alert("Error saving configuration");
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Dynamic Field Configuration
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddCircleOutlineIcon />}
        onClick={handleOpen}
        sx={{ mb: 2 }}
      >
        Add Field
      </Button>

      {/* Configured Fields Preview */}
      {fields.length === 0 ? (
        <Typography>No fields added yet</Typography>
      ) : (
        <Grid container spacing={2}>
          {fields.map((field, index) => (
            <Grid item xs={12} key={index}>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="subtitle1">{field.label}</Typography>
                  {field.type === "input" ? (
                    <TextField size="small" placeholder={field.label} />
                  ) : (
                    <TextField
                      select
                      size="small"
                      defaultValue=""
                      sx={{ minWidth: 150 }}
                    >
                      {field.options.map((opt, i) => (
                        <MenuItem key={i} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
                <Box>
                  <IconButton onClick={() => handleEdit(index)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDelete(index)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog for Add/Edit */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {editIndex !== null ? "Edit Field" : "Add New Field"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            fullWidth
            label="Field Type"
            value={currentField.type}
            onChange={(e) =>
              setCurrentField({ ...currentField, type: e.target.value })
            }
            margin="normal"
          >
            <MenuItem value="input">Input</MenuItem>
            <MenuItem value="select">Select</MenuItem>
          </TextField>

          <TextField
            fullWidth
            label="Label Name"
            value={currentField.label}
            onChange={(e) =>
              setCurrentField({ ...currentField, label: e.target.value })
            }
            margin="normal"
          />

          {currentField.type === "select" && (
            <TextField
              fullWidth
              label="Options (comma separated)"
              value={currentField.options}
              onChange={(e) =>
                setCurrentField({ ...currentField, options: e.target.value })
              }
              margin="normal"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldsConfig;
